/**
 * UsersFilterPanel Component - Filter panel for users table
 */
import React from 'react';
import { X } from 'lucide-react';
import { MultiSelectFilter } from '../MultiSelectFilter';
import { ROLE_OPTIONS } from '../../constants/roles';
import { RoleBadge } from '../RoleBadge';
import { type Cliente } from '../../services/clienteService';

interface UsersFilterPanelProps {
  isVisible: boolean;
  searchTerm: string;
  selectedRoles: string[];
  selectedStatus: string[];
  selectedClientes: number[];
  clientes: Cliente[];
  filterCount: number;
  onSearchChange: (term: string) => void;
  onRolesChange: (roles: string[]) => void;
  onStatusChange: (status: string[]) => void;
  onClientesChange: (clientes: number[]) => void;
  onClearAll: () => void;
}

export const UsersFilterPanel: React.FC<UsersFilterPanelProps> = ({
  isVisible,
  searchTerm,
  selectedRoles,
  selectedStatus,
  selectedClientes,
  clientes,
  filterCount,
  onSearchChange,
  onRolesChange,
  onStatusChange,
  onClientesChange,
  onClearAll,
}) => {
  if (!isVisible) return null;

  return (
    <div className="sticky top-[120px] z-10 bg-white dark:bg-gray-900 px-6 pb-6 animate-in slide-in-from-top duration-200">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-md">
        <div className="flex items-center justify-end mb-4">
          {filterCount > 0 && (
            <button
              onClick={onClearAll}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            >
              Limpar todos
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Role Filter */}
          <div className="flex-1 min-w-[200px]">
            <MultiSelectFilter
              label="Função"
              options={ROLE_OPTIONS}
              selectedValues={selectedRoles}
              onChange={onRolesChange}
              placeholder="Selecione funções..."
              showBadges={true}
            />
          </div>

          {/* Status Filter */}
          <div className="flex-1 min-w-[200px]">
            <MultiSelectFilter
              label="Status"
              options={[
                { value: 'ativo', label: 'Ativo' },
                { value: 'inativo', label: 'Inativo' },
              ]}
              selectedValues={selectedStatus}
              onChange={onStatusChange}
              placeholder="Selecione status..."
              showBadges={true}
            />
          </div>

          {/* Cliente Filter */}
          <div className="flex-1 min-w-[200px]">
            <MultiSelectFilter
              label="Cliente Vinculado"
              options={clientes.map(c => ({ value: c.id, label: c.nome_fantasia || c.razao_social }))}
              selectedValues={selectedClientes}
              onChange={onClientesChange}
              placeholder="Selecione clientes..."
              showBadges={true}
            />
          </div>
        </div>

        {/* Active Filter Chips */}
        {filterCount > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-2">
              {selectedRoles.map((role) => (
                <span key={role} className="inline-flex items-center gap-1">
                  <RoleBadge role={role as string} size="sm" />
                  <button
                    onClick={() => onRolesChange(selectedRoles.filter(r => r !== role))}
                    className="ml-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full p-1 text-red-600 dark:text-red-400 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}

              {selectedStatus.map((status) => (
                <span
                  key={status}
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                    status === 'ativo'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                  }`}
                >
                  {status === 'ativo' ? 'Ativo' : 'Inativo'}
                  <button
                    onClick={() => onStatusChange(selectedStatus.filter(s => s !== status))}
                    className="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}

              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                  Busca: "{searchTerm}"
                  <button
                    onClick={() => onSearchChange('')}
                    className="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersFilterPanel;
